"use client";

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

function useInView(threshold = 0.12) {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

interface ContactForm {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

export default function Contact() {
  const sec1 = useInView();
  const sec2 = useInView();
  const [form, setForm] = useState<ContactForm>({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const update = (field: keyof ContactForm, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock submit handler — connect to backend here
    setSubmitted(true);
  };

  const contactItems = [
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      label: '電郵地址',
      labelEn: 'Email',
      value: 'macklavelle14@gmail.com',
      href: 'mailto:macklavelle14@gmail.com',
      note: '一般查詢，2個工作日內回覆',
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      label: '工作室地址',
      labelEn: 'Address',
      value: '香港上環干諾道中 168-200 號信德中心',
      href: null,
      note: '請提前預約到訪',
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l4 2" />
        </svg>
      ),
      label: '辦公時間',
      labelEn: 'Hours',
      value: '週一至週六 09:00–18:00',
      href: null,
      note: '公眾假期休息',
    },
  ];

  return (
    <>
      <Header />

      {/* ── LIGHTWEIGHT HERO ─────────────────────────────── */}
      <section className="pt-32 pb-16 bg-primary relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 60% 50% at 80% 50%, rgba(201,169,110,0.12) 0%, transparent 70%)',
          }}
        />
        <div className="relative z-10 max-w-container mx-auto px-6 lg:px-12">
          <span className="font-mono text-xs text-accent/70 tracking-widest uppercase block mb-4">Contact Us</span>
          <h1 className="font-heading font-700 text-white mb-3" style={{ fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: '1.05' }}>
            聯繫我們
          </h1>
          <p className="font-body text-base text-white/50 max-w-lg">
            無論是查詢、諮詢還是合作提案，我們都很樂意聽你說。
          </p>
        </div>
      </section>

      {/* ── CONTACT CARDS + FORM ─────────────────────────── */}
      <section
        ref={sec1.ref as React.RefObject<HTMLElement>}
        className="py-24 bg-background"
      >
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left: Contact info */}
            <div>
              <div
                style={{
                  opacity: sec1.inView ? 1 : 0,
                  transform: sec1.inView ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'opacity 0.8s ease, transform 0.8s ease',
                }}
              >
                <span className="tag-pill mb-6 inline-block">Get in Touch</span>
                <h2 className="font-heading text-h3 font-700 text-primary mb-4">
                  隨時歡迎你聯繫
                </h2>
                <p className="font-body text-sm text-secondary leading-relaxed mb-10 max-w-sm">
                  我們相信好的設計始於好的溝通。無論你的想法有多初步，
                  都歡迎與我們分享。
                </p>
              </div>

              <div className="space-y-4">
                {contactItems.map((item, i) => (
                  <div
                    key={item.label}
                    className="flex items-start gap-5 p-5 bg-surface group hover:bg-primary transition-colors duration-300 hover-shine"
                    style={{
                      borderRadius: '4px',
                      opacity: sec1.inView ? 1 : 0,
                      transform: sec1.inView ? 'translateX(0)' : 'translateX(-20px)',
                      transition: `opacity 0.7s ease ${i * 0.1}s, transform 0.7s ease ${i * 0.1}s`,
                    }}
                  >
                    <div className="text-accent flex-shrink-0 mt-0.5 group-hover:text-accent transition-colors">
                      {item.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-mono text-xs text-muted group-hover:text-white/40 mb-0.5 transition-colors">
                        {item.label} · {item.labelEn}
                      </p>
                      {item.href ? (
                        <a
                          href={item.href}
                          target={item.href.startsWith('http') ? '_blank' : undefined}
                          rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="font-body text-base font-500 text-foreground group-hover:text-white hover:text-accent transition-colors block truncate"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="font-body text-base font-500 text-foreground group-hover:text-white transition-colors">
                          {item.value}
                        </p>
                      )}
                      <p className="font-body text-xs text-muted group-hover:text-white/40 mt-1 transition-colors">
                        {item.note}
                      </p>
                    </div>
                    {item.href && (
                      <svg
                        className="w-4 h-4 text-muted group-hover:text-accent flex-shrink-0 transition-colors"
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    )}
                  </div>
                ))}
              </div>

            </div>

            {/* Right: Contact form */}
            <div
              style={{
                opacity: sec1.inView ? 1 : 0,
                transform: sec1.inView ? 'translateX(0)' : 'translateX(30px)',
                transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
              }}
            >
              <div className="bg-surface p-8 lg:p-10" style={{ borderRadius: '4px' }}>
                {!submitted ? (
                  <>
                    <h3 className="font-heading text-xl font-700 text-primary mb-2">發送訊息</h3>
                    <p className="font-body text-sm text-muted mb-8">填寫以下表格，我們將盡快回覆您。</p>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="font-body text-sm font-600 text-primary block mb-2">
                            姓名 <span className="text-accent">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="您的姓名"
                            value={form.name}
                            onChange={(e) => update('name', e.target.value)}
                            required
                          />
                        </div>
                        <div>
                          <label className="font-body text-sm font-600 text-primary block mb-2">
                            電話
                          </label>
                          <input
                            type="tel"
                            className="form-input"
                            placeholder="+852 XXXX XXXX"
                            value={form.phone}
                            onChange={(e) => update('phone', e.target.value)}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="font-body text-sm font-600 text-primary block mb-2">
                          電郵地址 <span className="text-accent">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-input"
                          placeholder="your@email.com"
                          value={form.email}
                          onChange={(e) => update('email', e.target.value)}
                          required
                        />
                      </div>

                      <div>
                        <label className="font-body text-sm font-600 text-primary block mb-2">主題</label>
                        <select
                          className="form-input"
                          value={form.subject}
                          onChange={(e) => update('subject', e.target.value)}
                        >
                          <option value="">請選擇查詢類型</option>
                          <option>住宅設計查詢</option>
                          <option>商業空間查詢</option>
                          <option>報價查詢</option>
                          <option>合作提案</option>
                          <option>其他查詢</option>
                        </select>
                      </div>

                      <div>
                        <label className="font-body text-sm font-600 text-primary block mb-2">
                          訊息內容 <span className="text-accent">*</span>
                        </label>
                        <textarea
                          className="form-input resize-none"
                          rows={5}
                          placeholder="請描述您的需求或問題……"
                          value={form.message}
                          onChange={(e) => update('message', e.target.value)}
                          required
                        />
                      </div>

                      <button type="submit" className="btn-primary w-full justify-center">
                        發送訊息
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                        </svg>
                      </button>

                      <p className="font-mono text-xs text-muted text-center">
                        或直接發送至：
                        <a href="mailto:macklavelle14@gmail.com" className="text-accent hover:underline ml-1">
                          macklavelle14@gmail.com
                        </a>
                      </p>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-14 h-14 bg-accent/10 flex items-center justify-center mx-auto mb-5" style={{ borderRadius: '4px' }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C9A96E" strokeWidth="1.5">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="font-heading text-xl font-700 text-primary mb-2">訊息已送出！</h3>
                    <p className="font-body text-sm text-secondary mb-6">
                      感謝您的聯繫，我們將在2個工作日內回覆您。
                    </p>
                    <button onClick={() => setSubmitted(false)} className="btn-outline">
                      再次發送
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAP PLACEHOLDER + CTA ─────────────────────────── */}
      <section
        ref={sec2.ref as React.RefObject<HTMLElement>}
        className="py-20 bg-surface border-t border-border"
      >
        <div className="max-w-container mx-auto px-6 lg:px-12">
          {/* Map placeholder */}
          <div
            className="relative overflow-hidden mb-12 bg-primary/5 border border-border flex items-center justify-center"
            style={{
              borderRadius: '4px',
              height: '260px',
              opacity: sec2.inView ? 1 : 0,
              transition: 'opacity 0.8s ease',
            }}
          >
            <div className="text-center">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#C9A96E" strokeWidth="1" className="mx-auto mb-3">
                <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="font-heading text-lg font-600 text-primary">香港上環干諾道中 168-200 號信德中心</p>
              <p className="font-mono text-xs text-muted mt-1">Shun Tak Centre, 168-200 Connaught Road Central, Sheung Wan, Hong Kong</p>
              <a
                href="https://maps.google.com/?q=Shun+Tak+Centre,+168-200+Connaught+Road+Central,+Sheung+Wan,+Hong+Kong"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-body text-sm text-accent hover:underline mt-3"
              >
                在 Google Maps 查看
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>

          {/* CTA row */}
          <div
            className="flex flex-col md:flex-row items-center justify-between gap-6"
            style={{
              opacity: sec2.inView ? 1 : 0,
              transform: sec2.inView ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
            }}
          >
            <div>
              <h3 className="font-heading text-xl font-700 text-primary mb-1">
                準備好開始了嗎？
              </h3>
              <p className="font-body text-sm text-secondary">填寫諮詢表格，讓我們為您規劃專屬方案。</p>
            </div>
            <Link href="/registration-center" className="btn-accent flex-shrink-0">
              免費預約諮詢
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}